import {
  COLUMN_WIDTH_SLIGHT_CHANGE_PERCENT,
  GROUP_ROW_DETAIL,
  SUMMARY_INDEPENDENT_SPEC_KEYS,
} from "../types/constants";
import { inputColumnView } from "./inputColumnUtils";

export const buildColumnTypeMap = (columnsData) => {
  const map = {};
  (columnsData ?? []).forEach((col) => {
    const inputCol = inputColumnView(col);
    map[col.column] = {
      type: col._type ?? null,
      subType: col._subType ?? null,
      inputType: inputCol._type ?? null,
      inputSubType: inputCol._subType ?? null,
      isEnum: Boolean(col._options?.length),
    };
  });
  return map;
};

/** Returns a FLOAT column's decimals from columnDecimals, else its default decimals. */
export const resolveColumnDecimals = (col, columnDecimals) => {
  if (col._type !== "FLOAT") return undefined;
  const explicit = columnDecimals?.[col.column];
  if (explicit !== undefined && explicit !== null) return explicit;
  return col._defaultDecimals ?? undefined;
};

export const buildMetadataDisplayNameMap = (columnsData) => {
  const map = {};
  (columnsData ?? []).forEach((col) => {
    map[col.column] =
      col._canonicalDisplayName || col.displayName || col.column;
  });
  return map;
};

export const resolveColumnDisplayName = (
  columnName,
  columnNameOverrides,
  columnDefMap,
  metadataDisplayNameMap
) => {
  const override = columnNameOverrides[columnName];
  if (override === null)
    return metadataDisplayNameMap[columnName] || columnName;
  return (
    override ||
    columnDefMap[columnName]?.displayName ||
    metadataDisplayNameMap[columnName] ||
    columnName
  );
};

export const buildColumnDisplayNameMap = (
  columnDefs,
  columnNameOverrides,
  metadataDisplayNameMap
) => {
  const map = {};
  columnDefs.forEach((col) => {
    const override = columnNameOverrides[col.internalName];
    map[col.internalName] =
      override === null
        ? metadataDisplayNameMap[col.internalName] || col.internalName
        : override ||
          col.displayName ||
          metadataDisplayNameMap[col.internalName] ||
          col.internalName;
  });
  return map;
};

export const buildGridRows = (
  tableContents,
  statusOverrides,
  hideDetail,
  displayControlColumn
) => {
  const mapped = (tableContents ?? []).map((row, index) => ({
    ...row,
    id: String(index),
    status: statusOverrides[String(index)] ?? row.status,
  }));
  if (hideDetail && displayControlColumn) {
    return mapped.filter(
      (row) => row[displayControlColumn] !== GROUP_ROW_DETAIL
    );
  }
  return mapped;
};

export const checkHasDetailRows = (tableContents, displayControlColumn) => {
  if (!displayControlColumn) return false;
  return (tableContents ?? []).some(
    (row) => row[displayControlColumn] === GROUP_ROW_DETAIL
  );
};

export const buildSourceDisplayNameMap = (availableSources) => {
  const map = {};
  (availableSources || []).forEach((t) => {
    map[t.internalName] = t.displayName || t.internalName;
  });
  return map;
};

export const collectExistingColumnNames = (
  columnsData,
  columnNameOverrides,
  extraAggregateColumns
) => {
  const names = new Set();
  (columnsData ?? []).forEach((col) => {
    names.add(col.column);
    if (col.displayName) names.add(col.displayName);
  });
  Object.values(columnNameOverrides).forEach((v) => {
    if (v) names.add(v);
  });
  extraAggregateColumns.forEach((e) => names.add(e.column));
  return names;
};

export const formatSortItems = (sortModel) =>
  (sortModel ?? []).length === 0
    ? ["No sorting"]
    : sortModel.map((s) => `${s.field} (${s.sort ?? "asc"})`);

export const formatMetricItems = (items) =>
  (items ?? []).length === 0
    ? ["No metrics"]
    : items.map((m) => `${m.label ?? m.key} (${m.method})`);

export const formatGroupByItems = (
  groupByColumns,
  getDisplayName,
  describeColumn = () => null
) => {
  const colSet =
    groupByColumns instanceof Set
      ? groupByColumns
      : new Set(groupByColumns ?? []);
  return colSet.size === 0
    ? ["None"]
    : [...colSet].flatMap(
        (col) => describeColumn(col) ?? [getDisplayName(col)]
      );
};

export const formatRangeFilter = (
  saved,
  current,
  formatValue,
  rangeLabels,
  describeDynamic
) => {
  const [label1, label2] = rangeLabels;
  const isDate = label1 === "From";
  const key1 = isDate ? "from" : "min";
  const key2 = isDate ? "to" : "max";
  const isDynamic = (obj) => isDate && Boolean(obj?.dynamic);
  const isEmpty = (obj) =>
    !isDynamic(obj) && (!obj || (obj[key1] == null && obj[key2] == null));
  const describe = (obj) => {
    if (isEmpty(obj)) return ["No filter"];
    if (isDynamic(obj)) {
      const { name } = obj.dynamic;
      return [describeDynamic ? describeDynamic(name) : name];
    }
    const bounds = [
      [label1, obj[key1]],
      [label2, obj[key2]],
    ];
    return bounds
      .filter(([, value]) => isDate || value != null)
      .map(([label, value]) => `${label}: ${formatValue(value)}`);
  };
  return { savedState: describe(saved), currentState: describe(current) };
};

/** Returns one line per column whose width differs from its saved width, naming the column and the percentage change, in column order. */
export const describeColumnWidthChanges = ({
  savedWidths,
  currentWidths,
  columnOrder = [],
  defaultWidth,
  displayName,
  texts,
}) => {
  const columns = [
    ...new Set([
      ...Object.keys(savedWidths ?? {}),
      ...Object.keys(currentWidths ?? {}),
    ]),
  ];
  const position = (column) => {
    const index = columnOrder.indexOf(column);
    return index === -1 ? columnOrder.length : index;
  };
  return columns
    .sort((a, b) => position(a) - position(b))
    .flatMap((column) => {
      const saved = savedWidths?.[column] ?? defaultWidth(column);
      const current = currentWidths?.[column] ?? defaultWidth(column);
      if (!saved || saved === current) return [];
      const percent = ((current - saved) / saved) * 100;
      const increased = percent > 0;
      const change =
        Math.abs(percent) < COLUMN_WIDTH_SLIGHT_CHANGE_PERCENT
          ? increased
            ? texts.increasedSlightly
            : texts.reducedSlightly
          : `${increased ? texts.increased : texts.reduced} ${Math.round(
              Math.abs(percent)
            )}%`;
      return [`${displayName(column)}: ${change}`];
    });
};

export const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    if (Number.isNaN(value)) {
      return null;
    }
    return value;
  }
  if (typeof value === "boolean") {
    return value;
  }
  const textValue = String(value).trim();
  if (!textValue) {
    return null;
  }
  const numericCandidate = Number(textValue);
  if (!Number.isNaN(numericCandidate) && textValue !== "") {
    return numericCandidate;
  }
  return textValue.toLowerCase();
};

export const valuesMatch = (leftValue, rightValue) => {
  const left = normalizeComparableValue(leftValue);
  const right = normalizeComparableValue(rightValue);
  if (left === null || right === null) {
    return false;
  }
  if (typeof left === "number" && typeof right === "number") {
    return Math.abs(left - right) < 0.000001;
  }
  return left === right;
};

export const resolveSubmissionLayerIndex = (submission, row) => {
  const layers = submission?.state?.pricing?.layers;
  if (!Array.isArray(layers) || layers.length === 0) {
    return -1;
  }
  const layerIndex = Number(row.layerIndex);
  if (
    !Number.isNaN(layerIndex) &&
    layerIndex >= 0 &&
    layerIndex < layers.length
  ) {
    return layerIndex;
  }
  const layerIdCandidate = row.layerId ?? row.layerID ?? row.layerInternalId;
  if (layerIdCandidate != null) {
    const indexById = layers.findIndex(
      (layer) => String(layer?.id) === String(layerIdCandidate)
    );
    if (indexById >= 0) {
      return indexById;
    }
  }
  const layerReferenceCandidate = row.layerReference ?? row.reference;
  if (layerReferenceCandidate != null) {
    const indexByReference = layers.findIndex((layer) => {
      const layerReference = layer?.layerReference ?? layer?.reference;
      return String(layerReference ?? "") === String(layerReferenceCandidate);
    });
    if (indexByReference >= 0) {
      return indexByReference;
    }
  }
  const exactFieldPairs = [
    ["attachment", "attachment"],
    ["limit", "limit"],
    ["premium", "premium"],
    ["brokeragePremium", "brokeragePremium"],
    ["aggregateLimit", "aggregateLimit"],
    ["layerReference", "layerReference"],
    ["reference", "layerReference"],
    ["policyType", "policyType"],
    ["paperProvider", "paperProvider"],
    ["bound", "bound"],
    ["triaStatus", "triaStatus"],
  ];
  const usableFieldPairs = exactFieldPairs.filter(([rowField]) => {
    return normalizeComparableValue(row?.[rowField]) !== null;
  });
  if (usableFieldPairs.length > 0) {
    const rankedMatches = layers
      .map((layer, index) => {
        const score = usableFieldPairs.reduce(
          (count, [rowField, layerField]) => {
            return (
              count + (valuesMatch(row[rowField], layer?.[layerField]) ? 1 : 0)
            );
          },
          0
        );
        return {
          index,
          score,
        };
      })
      .filter(({ score }) => score > 0)
      .sort((first, second) => second.score - first.score);
    if (rankedMatches.length > 0) {
      const topMatch = rankedMatches[0];
      const nextMatch = rankedMatches[1];
      const isUniqueTopMatch = !nextMatch || nextMatch.score < topMatch.score;
      if (isUniqueTopMatch) {
        return topMatch.index;
      }
    }
  }
  if (layers.length === 1) {
    return 0;
  }
  return -1;
};

/** Fingerprints the parts of a report and asAt that determine its summary. */
export const summaryFingerprint = (report, asAt) =>
  JSON.stringify(
    {
      specification: report?.specification ?? report ?? null,
      asAt: asAt ?? null,
    },
    (key, value) =>
      SUMMARY_INDEPENDENT_SPEC_KEYS.includes(key) ? undefined : value
  );
